/**
 * Classe do veículo para tarifação (#396).
 *
 * As quatro vêm dos quadros transcritos na issue: a Emurb cobra automóvel,
 * motocicleta e caminhonete separados; a Tarifa Avulso acrescenta bicicleta.
 *
 * `carro` é o padrão em todo lugar. Ticket sem classe e regra sem classe
 * continuam significando o que sempre significaram, então nada que já existe
 * muda de comportamento.
 */
export const vehicleClasses = ['carro', 'moto', 'grande', 'bicicleta'] as const;

export type VehicleClass = (typeof vehicleClasses)[number];

export const DEFAULT_VEHICLE_CLASS: VehicleClass = 'carro';

/** Rótulos do painel e do app, na língua de quem toca estacionamento. */
export const VEHICLE_CLASS_LABELS: Record<VehicleClass, string> = {
  carro: 'Carro',
  moto: 'Moto',
  grande: 'Grande porte',
  bicicleta: 'Bicicleta',
};

/** O veículo do motorista nas mensagens: "Sua moto", não "Seu carro" (#512). */
export const VEHICLE_POSSESSIVE: Record<VehicleClass, string> = {
  carro: 'Seu carro',
  moto: 'Sua moto',
  grande: 'Seu veículo',
  bicicleta: 'Sua bicicleta',
};

/**
 * Classe efetiva de um ticket. `null` é carro — a coluna nasceu depois dos
 * tickets que já existiam, e eles são carros.
 */
export const vehicleClassOf = (value: string | null | undefined): VehicleClass =>
  value && (vehicleClasses as readonly string[]).includes(value)
    ? (value as VehicleClass)
    : DEFAULT_VEHICLE_CLASS;

/**
 * As regras que um veículo desta classe paga (#396): a regra da classe vence
 * a genérica do mesmo `kind`, e uma regra de outra classe fica de fora. É a
 * mesma escolha no fechamento, no recibo ao vivo, na tabela impressa e na
 * simulação do painel, para nenhum deles mostrar um valor diferente (#529).
 */
export const rulesForClass = <T extends { kind: string; vehicleClass: string | null }>(
  rules: readonly T[],
  vehicleClass: VehicleClass,
): T[] => {
  const byKind = new Map<string, T>();
  for (const rule of rules) {
    if (rule.vehicleClass !== null && rule.vehicleClass !== vehicleClass) continue;
    const current = byKind.get(rule.kind);
    // Específica da classe ganha da genérica; entre duas iguais, a última vence.
    if (!current || (current.vehicleClass === null && rule.vehicleClass !== null)) {
      byKind.set(rule.kind, rule);
    }
  }
  return [...byKind.values()];
};
